import React, { useEffect, useMemo, useState } from 'react'
import { Card, Dropdown, Menu, Tree, theme } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { RootCategory } from '../../services/models'
import {
  buildCatalogTreeData,
  catalogExpandedKeys,
  isServiceKey,
  SERVICE_KEY_PREFIX
} from '../CatalogTree/catalogTree'

type Props = {
  categories: RootCategory[]
}

// Ширину попапа задаём явно: дерево в 320px — это ширина, при которой имена
// сервисов не переносятся.
const DROPDOWN_WIDTH = 320

const TopMenuComponent: React.FC<Props> = ({ categories }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = theme.useToken()
  const [open, setOpen] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() => catalogExpandedKeys(categories))

  useEffect(() => {
    setExpandedKeys(catalogExpandedKeys(categories))
  }, [categories])

  const treeData = useMemo(() => buildCatalogTreeData(categories), [categories])

  const parts = location.pathname.split('/')
  // И /analytics/{serviceId}, и /analytics/{serviceId}/research/{researchId}
  // относятся к одному сервису, поэтому подсветка берётся из третьего сегмента.
  const isAnalyticsSection = parts[1] === 'analytics'
  const selectedKeys = isAnalyticsSection && parts[2] ? [`${SERVICE_KEY_PREFIX}${parts[2]}`] : []
  // Активный пункт полосы — первый сегмент пути: /analytics и /data устроены
  // по-разному, но оба живут на верхнем уровне.
  const topLevelKey = parts[1] === 'analytics' || parts[1] === 'data' ? parts[1] : ''

  // popupRender подменяет содержимое оверлея целиком, поэтому стили
  // .ant-dropdown-menu (фон, скругление, boxShadowSecondary) к попапу не
  // применяются — по умолчанию он плоский. Рисуем их сами через токены темы.
  const dropdown = (
    <Card
      size="small"
      styles={{ body: { padding: 8 } }}
      style={{
        width: DROPDOWN_WIDTH,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary
      }}
    >
      <Tree
        showLine
        blockNode
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={(keys) => setExpandedKeys(keys as string[])}
        selectedKeys={selectedKeys}
        onSelect={(keys) => {
          const key = keys[0] as string | undefined
          // Категории и подкатегории только раскрывают ветку, переход
          // делают сервисы — так же ведёт себя дерево на странице каталога.
          if (!key || !isServiceKey(key)) {
            return
          }
          setOpen(false)
          navigate(key)
        }}
      />
    </Card>
  )

  // Пункт «Аналитика» — только триггер дерева: его label лежит внутри Dropdown,
  // поэтому клик по нему обрабатывается самим Dropdown, а в onClick мы его
  // игнорируем, чтобы не уводить со страницы каталога. Фокус и Enter при этом
  // остаются на li, который рисует Menu.
  return (
    <Menu mode="horizontal"
          theme="dark"
          style={{ borderBottom: 0 }}
          selectedKeys={topLevelKey ? [topLevelKey] : []}
          onClick={(e) => {
            if (e.key === 'analytics') return
            navigate('/' + e.key)
          }}
          items={[
            {
              key: 'analytics',
              label: (
                <Dropdown open={open}
                          onOpenChange={setOpen}
                          popupRender={() => dropdown}>
                  <div style={{ margin: '0 -15px', padding: '0 15px' }}>
                    Аналитика
                    <DownOutlined style={{ fontSize: 10, marginLeft: 4 }}/>
                  </div>
                </Dropdown>
              )
            },
            { key: 'data', label: 'Данные' }
          ]}/>
  )
}

export default TopMenuComponent
